import { ArrowRight, CheckCircle } from "lucide-react";

import { Link } from "react-router";

import type { SolutionEntry } from "../data/solutions";

import { CCTV_ADDON_PLAN_NOTE, CCTV_SOLUTION_IDS } from "../data/planCatalog";

import { CctvHighlightBlock } from "./CctvHighlightBlock";
import { WebcamHighlightBlock } from "./WebcamHighlightBlock";



type SolutionSectionProps = {

  sol: SolutionEntry;

  /** Index in list for alternating layout on hub page */

  index: number;

  /** On dedicated topic pages the hero already shows the title */

  hideTitle?: boolean;

};



export function SolutionSection({ sol, index, hideTitle }: SolutionSectionProps) {

  const isCctvProductPage = CCTV_SOLUTION_IDS.has(sol.id);



  return (

    <div

      id={sol.id}

      className={`rounded-2xl border ${sol.borderColor} overflow-hidden`}

      style={{ background: "linear-gradient(135deg, var(--color-slate-900) 0%, var(--color-slate-950) 100%)" }}

    >

      <div className={`grid lg:grid-cols-2 gap-0`}>

        <div className={`p-10 lg:p-14 ${index % 2 === 1 ? "lg:order-2" : ""}`}>

          <div className="flex items-center gap-3 mb-8">

            <div

              className={`w-12 h-12 rounded-xl ${sol.iconBg} flex items-center justify-center shadow-lg ${sol.iconShadow}`}

            >

              <sol.icon size={22} className="text-white" />

            </div>

            <span

              className={`text-xs uppercase tracking-widest ${

                sol.accent === "teal" ? "text-teal-400" : "text-emerald-400"

              }`}

              style={{ fontWeight: 600 }}

            >

              {sol.tag}

            </span>

          </div>



          {!hideTitle && (

            <>

              <h2

                className="text-slate-100 mb-4"

                style={{ fontSize: "1.8rem", fontWeight: 800, letterSpacing: "-0.02em", lineHeight: "1.15" }}

              >

                {sol.title}

              </h2>

              <p

                className={`mb-6 text-base ${sol.accent === "teal" ? "text-teal-300" : "text-emerald-300"}`}

                style={{ fontWeight: 500 }}

              >

                {sol.subtitle}

              </p>

            </>

          )}

          <p className="text-on-dark-muted leading-relaxed mb-8 text-sm">{sol.description}</p>



          <div className="flex flex-col gap-3">

            {sol.benefits.map((b) => (

              <div key={b} className="flex items-start gap-3">

                <CheckCircle

                  size={16}

                  className={`mt-0.5 shrink-0 ${

                    sol.accent === "teal" ? "text-teal-400" : "text-emerald-400"

                  }`}

                />

                <span className="text-slate-300 text-sm">{b}</span>

              </div>

            ))}

          </div>

        </div>



        <div

          className={`p-10 lg:p-14 border-t lg:border-t-0 ${

            index % 2 === 1 ? `lg:border-r ${sol.borderColor} lg:order-1` : `lg:border-l ${sol.borderColor}`

          }`}

          style={{

            background:

              sol.accent === "teal"

                ? "linear-gradient(135deg, rgba(13, 148, 136, 0.06) 0%, transparent 60%)"

                : "linear-gradient(135deg, rgba(5, 150, 105, 0.06) 0%, transparent 60%)",

          }}

        >

          <p className="text-on-dark-muted text-xs uppercase tracking-widest mb-6" style={{ fontWeight: 600 }}>

            Common Use Cases

          </p>

          <div className="flex flex-col gap-4">

            {sol.useCases.map((uc) => (

              <div

                key={uc}

                className={`flex items-center gap-3 p-4 rounded-xl border ${sol.borderColor} bg-slate-900/50`}

              >

                <ArrowRight

                  size={14}

                  className={

                    sol.accent === "teal" ? "text-teal-400 shrink-0" : "text-emerald-400 shrink-0"

                  }

                />

                <span className="text-slate-300 text-sm">{uc}</span>

              </div>

            ))}

          </div>

        </div>

      </div>



      {isCctvProductPage && (

        <div className="px-10 lg:px-14 pb-10 lg:pb-14 border-t border-amber-500/15">

          <div className="mt-8 rounded-xl border border-amber-400/25 bg-amber-400/5 px-4 py-3">

            <p className="text-amber-100/80 text-sm leading-relaxed">{CCTV_ADDON_PLAN_NOTE}</p>

            <Link

              to="/pricing#cctv-plugin"

              className="inline-flex items-center gap-1 text-teal-400 hover:text-teal-300 text-sm font-medium mt-2 transition-colors"

            >

              See CCTV+ pricing

              <ArrowRight size={14} />

            </Link>

          </div>

        </div>

      )}



      {sol.cctvHighlight && !isCctvProductPage && (

        <div className="px-10 lg:px-14 pb-10 lg:pb-14 border-t border-amber-500/15">

          <CctvHighlightBlock {...sol.cctvHighlight} className="mt-8" />

        </div>

      )}

      {sol.webcamHighlight && (

        <div className="px-10 lg:px-14 pb-10 lg:pb-14 border-t border-violet-500/15">

          <WebcamHighlightBlock {...sol.webcamHighlight} className="mt-8" />

        </div>

      )}

    </div>

  );

}

